import { Routes } from '@angular/router';
import { authGuard, guestGuard, vendorGuard, adminGuard, affiliateGuard, nonAdminGuard, taxProfileGuard } from './core/guards/auth.guard';

export const routes: Routes = [
  // ── Public landing (everyone) ───────────────────────
  {
    path: '',
    pathMatch: 'full',
    loadComponent: () =>
      import('./features/public/pages/landing.component').then((m) => m.LandingComponent),
  },

  // ── Legal pages (public, no guard) ──────────────────
  {
    path: 'legal',
    children: [
      {
        path: 'terms',
        loadComponent: () =>
          import('./features/public/pages/terms.component').then((m) => m.TermsComponent),
      },
      {
        path: 'privacy',
        loadComponent: () =>
          import('./features/public/pages/privacy.component').then((m) => m.PrivacyComponent),
      },
    ],
  },

  // ── OAuth callback (no guard) ───────────────────────
  {
    path: 'oauth-callback',
    loadComponent: () =>
      import('./features/auth/pages/oauth-callback.component').then((m) => m.OAuthCallbackComponent),
  },

  // ── Affiliate referral landing (public, no guard) ───
  {
    path: 'ref/:code',
    loadComponent: () =>
      import('./features/affiliate/pages/ref-landing.component').then((m) => m.RefLandingComponent),
  },

  // ── Auth pages (public, no sidebar) ─────────────────
  {
    path: 'accept-invitation',
    loadComponent: () =>
      import('./features/auth/pages/accept-invitation.component').then((m) => m.AcceptInvitationComponent),
  },
  {
    path: 'auth',
    children: [
      {
        path: 'login',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/auth/pages/login.component').then((m) => m.LoginComponent),
      },
      {
        path: 'register',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/auth/pages/register.component').then((m) => m.RegisterComponent),
      },
      {
        path: 'forgot-password',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./features/auth/pages/forgot-password.component').then((m) => m.ForgotPasswordComponent),
      },
      {
        path: 'onboarding',
        canActivate: [authGuard],
        loadComponent: () =>
          import('./features/auth/pages/onboarding.component').then((m) => m.OnboardingComponent),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },

  // Top-level aliases for direct URL access
  { path: 'login', redirectTo: 'auth/login', pathMatch: 'full' },
  { path: 'register', redirectTo: 'auth/register', pathMatch: 'full' },
  { path: 'dashboard', redirectTo: 'app/search', pathMatch: 'full' },
  { path: 'app/dashboard', redirectTo: 'app/search', pathMatch: 'full' },

  // ── Protected routes (with sidebar shell) ───────────
  {
    path: 'app',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./layout/shell.component').then((m) => m.ShellComponent),
    children: [
      // Search — accessible by all authenticated users (CAMs primarily).
      {
        path: 'search',
        loadComponent: () =>
          import('./features/search/pages/search.component').then((m) => m.SearchComponent),
      },
      // Vendor Profile
      {
        path: 'vendors/:id',
        loadComponent: () =>
          import('./features/search/pages/vendor-profile.component').then((m) => m.VendorProfileComponent),
      },
      // Messaging UI
      {
        path: 'messages',
        canActivate: [nonAdminGuard],
        loadComponent: () =>
          import('./features/messaging/pages/messaging.component').then((m) => m.MessagingComponent),
      },
      // RFP Flows
      {
        path: 'rfp',
        canActivate: [nonAdminGuard],
        children: [
          {
            path: 'create',
            loadComponent: () =>
              import('./features/rfp/pages/rfp-wizard.component').then((m) => m.RfpWizardComponent),
          },
          {
            path: 'response',
            loadComponent: () =>
              import('./features/rfp/pages/rfp-response.component').then((m) => m.RfpResponseComponent),
          },
        ]
      },

      // Organization Management (CAM only)
      {
        path: 'organization',
        canActivate: [nonAdminGuard],
        loadComponent: () =>
          import('./features/properties/pages/organization.component').then((m) => m.OrganizationComponent),
      },

      // Properties (CAM only)
      {
        path: 'properties',
        canActivate: [nonAdminGuard],
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('./features/properties/pages/property-list.component').then((m) => m.PropertyListComponent),
          },
          {
            path: 'new',
            loadComponent: () =>
              import('./features/properties/pages/property-form.component').then((m) => m.PropertyFormComponent),
          },
          {
            path: ':id/edit',
            loadComponent: () =>
              import('./features/properties/pages/property-form.component').then((m) => m.PropertyFormComponent),
          },
          {
            path: ':id',
            loadComponent: () =>
              import('./features/properties/pages/property-detail.component').then((m) => m.PropertyDetailComponent),
          },
        ],
      },

      // Vendor portal
      {
        path: 'vendor',
        canActivate: [vendorGuard],
        children: [
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./features/vendor/pages/dashboard.component').then((m) => m.VendorDashboardComponent),
          },
          {
            path: 'classify',
            redirectTo: 'services',
            pathMatch: 'full'
          },
          {
            path: 'services',
            loadComponent: () =>
              import('./features/vendor/pages/services.component').then((m) => m.ServicesComponent),
          },
          {
            path: 'history',
            loadComponent: () =>
              import('./features/vendor/pages/history.component').then((m) => m.HistoryComponent),
          },
          {
            path: 'boost',
            redirectTo: 'services',
            pathMatch: 'full'
          },
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        ],
      },

      // Affiliate portal — open to any authenticated user
      {
        path: 'affiliate',
        canActivate: [affiliateGuard],
        children: [
          // Hard gate: no dashboard/referral-link access until the tax profile is certified.
          // Kept out of taxProfileGuard so the affiliate can always reach this page.
          {
            path: 'tax-profile-setup',
            loadComponent: () =>
              import('./features/affiliate/pages/tax-profile-setup.component').then((m) => m.AffiliateTaxProfileSetupComponent),
          },
          {
            path: 'dashboard',
            canActivate: [taxProfileGuard],
            loadComponent: () =>
              import('./features/affiliate/pages/dashboard.component').then((m) => m.AffiliateDashboardComponent),
          },
          {
            path: 'csv-upload',
            canActivate: [taxProfileGuard],
            loadComponent: () =>
              import('./features/affiliate/pages/csv-upload.component').then((m) => m.AffiliateCsvUploadComponent),
          },
          {
            path: 'payouts',
            canActivate: [taxProfileGuard],
            loadComponent: () =>
              import('./features/affiliate/pages/payouts.component').then((m) => m.AffiliatePayoutsComponent),
          },
          {
            path: 'payout-setup',
            canActivate: [taxProfileGuard],
            loadComponent: () =>
              import('./features/affiliate/pages/payout-setup.component').then((m) => m.AffiliatePayoutSetupComponent),
          },
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        ],
      },

      // Admin panel
      {
        path: 'admin',
        canActivate: [adminGuard],
        children: [
          {
            path: 'dashboard',
            loadComponent: () =>
              import('./features/admin/pages/dashboard.component').then((m) => m.AdminDashboardComponent),
          },
          {
            path: 'geography',
            loadComponent: () =>
              import('./features/admin/pages/geography.component').then((m) => m.AdminGeographyComponent),
          },
          {
            path: 'users',
            loadComponent: () =>
              import('./features/admin/pages/users.component').then((m) => m.AdminUsersComponent),
          },
          {
            path: 'taxonomy',
            loadComponent: () =>
              import('./features/admin/pages/taxonomy.component').then((m) => m.TaxonomyAdminComponent),
          },
          {
            path: 'queue',
            loadComponent: () =>
              import('./features/admin/pages/queue.component').then((m) => m.QueueComponent),
          },
          {
            path: 'affiliate-payouts',
            loadComponent: () =>
              import('./features/admin/pages/affiliate-payouts.component').then((m) => m.AffiliatePayoutsAdminComponent),
          },
          {
            path: 'ranking',
            loadComponent: () =>
              import('./features/admin/pages/ranking.component').then((m) => m.AdminRankingComponent),
          },
          { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
        ],
      },

      { path: '', redirectTo: 'search', pathMatch: 'full' },
    ],
  },

  // Fallback — send unknown URLs to the public landing
  { path: '**', redirectTo: '' },
];
